/**
 * Patient Registration with Informed Consent workflow.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const kenyanCounties = [
  'Baringo', 'Bomet', 'Bungoma', 'Busia', 'Elgeyo-Marakwet', 'Embu', 'Garissa',
  'Homa Bay', 'Isiolo', 'Kajiado', 'Kakamega', 'Kericho', 'Kiambu', 'Kilifi',
  'Kirinyaga', 'Kisii', 'Kisumu', 'Kitui', 'Kwale', 'Laikipia', 'Lamu', 'Machakos',
  'Makueni', 'Mandera', 'Marsabit', 'Meru', 'Migori', 'Mombasa', 'Murang\'a',
  'Nairobi', 'Nakuru', 'Nandi', 'Narok', 'Nyamira', 'Nyandarua', 'Nyeri', 'Samburu',
  'Siaya', 'Taita-Taveta', 'Tana River', 'Tharaka-Nithi', 'Trans-Nzoia', 'Turkana',
  'Uasin Gishu', 'Vihiga', 'Wajir', 'West Pokot'
];

const chronicConditions = [
  'Diabetes (Type 1)', 'Diabetes (Type 2)', 'Hypertension', 'Asthma',
  'Heart Disease', 'Kidney Disease', 'Cancer', 'HIV/AIDS',
  'Arthritis', 'COPD', 'Epilepsy', 'Sickle Cell Disease', 'Other'
];

const PatientRegister = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone_number: '',
    date_of_birth: '',
    gender: '',
    county: '',
    sub_county: '',
    chronic_conditions: [],
    consent_given: false,
  });
  
  const { signup, login, registerProfile } = useAuth();
  const navigate = useNavigate();

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleCondition = (condition) => {
    setFormData(prev => ({
      ...prev,
      chronic_conditions: prev.chronic_conditions.includes(condition)
        ? prev.chronic_conditions.filter(c => c !== condition)
        : [...prev.chronic_conditions, condition]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent_given) {
      toast.error('You must provide informed consent to register.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);

    try {
      let authUser;

      // Create Firebase Auth account
      try {
        authUser = await signup(formData.email, formData.password, formData.full_name);
      } catch (signupErr) {
        if (signupErr.code === 'auth/email-already-in-use') {
          authUser = await login(formData.email, formData.password);
        } else {
          throw signupErr;
        }
      }
      
      // Normalize phone number to E.164 (Kenyan local 07xx → +25407xx)
      const rawPhone = (formData.phone_number || '').trim().replace(/[\s-]/g, '');
      const normalizedPhone = rawPhone.startsWith('+') ? rawPhone
        : rawPhone.startsWith('0') ? '+254' + rawPhone.slice(1)
        : rawPhone.startsWith('254') ? '+' + rawPhone
        : '+254' + rawPhone;

      // Register profile in backend
      await registerProfile({
        email: formData.email,
        full_name: formData.full_name,
        phone_number: normalizedPhone,
        role: 'patient',
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        county: formData.county,
        sub_county: formData.sub_county,
        chronic_conditions: formData.chronic_conditions,
        consent_given: true,
      }, authUser);

      toast.success('Registration successful! Welcome to CDCS.');
      navigate('/patient/dashboard');
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessages = {
        'auth/email-already-in-use': 'An account with this email already exists.',
        'auth/weak-password': 'Password is too weak. Use at least 6 characters.',
        'auth/invalid-email': 'Please enter a valid email address.',
      };
      toast.error(errorMessages[err.code] || err.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-blue-50">
      <div className="max-w-md mx-auto px-6 pt-8 pb-8">
        <Link to="/patient/login" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Login
        </Link>

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Patient Registration</h1>
          <p className="text-gray-500 mt-1">Step {step} of 3</p>
          {/* Progress bar */}
          <div className="flex gap-2 mt-3">
            {[1, 2, 3].map(s => (
              <div key={s} className={`flex-1 h-1.5 rounded-full ${s <= step ? 'bg-primary-500' : 'bg-gray-200'}`} />
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Account Information */}
          {step === 1 && (
            <div className="card space-y-4">
              <h2 className="section-title">Account Information</h2>
              
              <div>
                <label className="label">Full Name *</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={e => updateField('full_name', e.target.value)}
                  className="input-field"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div>
                <label className="label">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => updateField('email', e.target.value)}
                  className="input-field"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="label">Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone_number}
                  onChange={e => updateField('phone_number', e.target.value)}
                  className="input-field"
                  placeholder="0712345678"
                  required
                />
              </div>

              <div>
                <label className="label">Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={e => updateField('password', e.target.value)}
                  className="input-field"
                  placeholder="Min. 6 characters"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="label">Confirm Password *</label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={e => updateField('confirmPassword', e.target.value)}
                  className="input-field"
                  placeholder="Re-enter password"
                  required
                />
              </div>

              <button
                type="button"
                onClick={() => {
                  if (!formData.full_name || !formData.email || !formData.password) {
                    toast.error('Please fill all required fields.');
                    return;
                  }
                  setStep(2);
                }}
                className="btn-primary w-full"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Personal & Medical Info */}
          {step === 2 && (
            <div className="card space-y-4">
              <h2 className="section-title">Personal & Medical Information</h2>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Date of Birth</label>
                  <input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={e => updateField('date_of_birth', e.target.value)}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="label">Gender</label>
                  <select
                    value={formData.gender}
                    onChange={e => updateField('gender', e.target.value)}
                    className="input-field"
                  >
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">County</label>
                <select
                  value={formData.county}
                  onChange={e => updateField('county', e.target.value)}
                  className="input-field"
                >
                  <option value="">Select County</option>
                  {kenyanCounties.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Sub-County</label>
                <input
                  type="text"
                  value={formData.sub_county}
                  onChange={e => updateField('sub_county', e.target.value)}
                  className="input-field"
                  placeholder="Enter sub-county"
                />
              </div>

              <div>
                <label className="label">Chronic Conditions (select all that apply)</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {chronicConditions.map(condition => (
                    <button
                      key={condition}
                      type="button"
                      onClick={() => toggleCondition(condition)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                        formData.chronic_conditions.includes(condition)
                          ? 'bg-primary-100 border-primary-300 text-primary-700'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {condition}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)} className="btn-secondary flex-1">
                  Back
                </button>
                <button type="button" onClick={() => setStep(3)} className="btn-primary flex-1">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Informed Consent */}
          {step === 3 && (
            <div className="card space-y-4">
              <h2 className="section-title">Informed Consent</h2>
              
              <div className="bg-blue-50 rounded-xl p-4 text-sm text-gray-700 max-h-60 overflow-y-auto">
                <h3 className="font-semibold mb-2">Data Collection & Processing Consent</h3>
                <p className="mb-2">
                  By registering with the Chronic Diseases Consultation System (CDCS), I understand and consent to the following:
                </p>
                <ol className="list-decimal list-inside space-y-2">
                  <li>My personal and medical information will be collected and stored securely for the purpose of providing healthcare consultations.</li>
                  <li>My data will be encrypted both at rest and in transit to protect my privacy.</li>
                  <li>Only authorized healthcare providers assigned to my care will have access to my medical records.</li>
                  <li>I can request access to, correction of, or deletion of my personal data at any time.</li>
                  <li>My data may be used in anonymized form for healthcare research and system improvement.</li>
                  <li>SMS and email notifications will be sent to me regarding appointments, prescriptions, and important health updates.</li>
                  <li>Video consultations may be conducted as part of my healthcare, and I consent to participate in them.</li>
                </ol>
                <p className="mt-3 font-medium">
                  This consent is given in accordance with the Kenya Data Protection Act, 2019.
                </p>
              </div>

              <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.consent_given}
                  onChange={e => updateField('consent_given', e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-primary-500 focus:ring-primary-400"
                />
                <span className="text-sm text-gray-700">
                  I have read, understood, and agree to the data collection and processing terms outlined above. 
                  I provide my informed consent for registration.
                </span>
              </label>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(2)} className="btn-secondary flex-1">
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.consent_given}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {loading && <div className="spinner w-5 h-5 border-2"></div>}
                  Complete Registration
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PatientRegister;
